import { PortalTelemetryEvent } from '../../../app/telemetry/portalTelemetryEvents';
import { PortalTelemetryScreen } from '../../../app/telemetry/portalTelemetryScreens';
import { validationIssues } from '../../../app/telemetry/validationTelemetry';
import { portalAction, portalScreenContext, trackPortalEvent } from '../../../app/telemetry/portalTelemetry';
import { usePortalScreen } from '../../../app/telemetry/usePortalTelemetry';
import { Button, Grid, Typography } from "@mui/material";
import { useFormContext } from "react-hook-form";
import { Link, useHistory, useParams } from "react-router-dom";
import { useReferenceData } from "../../../app/customHooks/useReferenceData";
import { ApiRequestStatus } from "../../../app/enums/apiRequestStatus";
import LoadingComponent from "../../../app/layout/LoadingComponent";
import { useAppDispatch, useAppSelector } from "../../../app/store/configureStore";
import { API_REQUEST_FULLFILLED, MESSAGE_DETAILS_SAVED, MESSAGE_PROCESSING_DETAILS } from "../../../app/utils/constant";
import AppTextareaInput from "../../../app/components/AppTextareaInput";
import { declineReviewAsync, submitReviewAsync } from "../../../app/slices/pcsPdpSlice";
import { toast } from "react-toastify";
import pcsPlanFormModel from "../../pcsPlan/common/pcsPlanFormModel";
import { ReviewPcsPdpCommandDto } from "../../pcsPlan/models/reviewPcsPdpCommandDto";
import { AppPathEnum } from "../../../app/enums/appPathEnum";
import { useEffect, useState } from "react";
import UnsavedChangesDialog from "../../../app/components/UnsavedChangesDialog";

export default function ReviewerComments() {
    const { ReviewerComments } = pcsPlanFormModel.formField;
    const { enrolmentYearId } = useParams<{ enrolmentYearId: string }>();
    const { control, getValues, reset, trigger, getFieldState } = useFormContext();
    const history = useHistory();

    const dispatch = useAppDispatch();

    const { pcsEnrolmentYears, submitReviewStatus, declineReviewStatus } = useAppSelector(state => state.pcsPdp);

    useReferenceData();

    const [declineReviewConfirmation, setDeclineReviewConfirmation] = useState(false);

    useEffect(() => {
        if (pcsEnrolmentYears && pcsEnrolmentYears.length > 0) {
            const currentEnrolmentYear = pcsEnrolmentYears?.find(x => x.enrolmentYearId === enrolmentYearId);
            reset({
                [ReviewerComments.name]: currentEnrolmentYear?.pdpReviewerComments
            });
        }
    }, [pcsEnrolmentYears, enrolmentYearId, reset]);

    const mapToReviewerCommentsDto = (isSubmitted: boolean) => {
        const data: ReviewPcsPdpCommandDto = {
            enrolmentYearId: enrolmentYearId,
            PdpReviewerComments: getValues([ReviewerComments.name]).toString(),
            submit: isSubmitted
        }
        return data;
    }

    const submitReviewerComments = async (isSubmitted: boolean) => {
        const isStepValid = await trigger();
        if (!isStepValid) {
            trackPortalEvent(portalScreenContext(PortalTelemetryScreen.ReviewerComments),
                PortalTelemetryEvent.ValidationBlocked, {
                step: undefined,
                validationErrors: validationIssues(PortalTelemetryScreen.ReviewerComments, getFieldState)
            });
        }

        if (isStepValid) {
            let reviewerCommentsDto = mapToReviewerCommentsDto(isSubmitted);
            dispatch(submitReviewAsync(reviewerCommentsDto)).then((response: any) => {
                if (response.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
                    toast.success(MESSAGE_DETAILS_SAVED);
                    history.push(`${AppPathEnum.PCS_PLAN_REVIEW}${window.location.search}`);
                }
            });
        }
    };

    const declineReview = async () => {
        dispatch(declineReviewAsync(enrolmentYearId)).then((response: any) => {
            if (response.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
                toast.success("Review request declined.");
                history.push(`${AppPathEnum.PCS_PLAN_REVIEW}${window.location.search}`);
            }
        });
    };

    usePortalScreen(portalScreenContext(PortalTelemetryScreen.ReviewerComments), undefined, !((submitReviewStatus === ApiRequestStatus.Pending ||
        declineReviewStatus === ApiRequestStatus.Pending)));

    if (submitReviewStatus === ApiRequestStatus.Pending ||
        declineReviewStatus === ApiRequestStatus.Pending) {
        return <LoadingComponent message={MESSAGE_PROCESSING_DETAILS} />
    }

    return <>
        <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'left' }}>
            <Typography variant='h5' sx={{ fontWeight: '550' }}>Reviewer comments</Typography>
        </Grid>

        <Grid size={{ xs: 12 }}>
            <AppTextareaInput control={control} name={ReviewerComments.name} label={ReviewerComments.label}
                maxStringCount={ReviewerComments.maxStringCount} />
        </Grid>

        <Grid size={{ xs: 12, sm: 5, md: 3, lg: 2.5 }}>
            <Button variant="contained" color="warning" onClick={portalAction(portalScreenContext(PortalTelemetryScreen.ReviewerComments), `${PortalTelemetryScreen.ReviewerComments}.onClick.submitReviewerComments.draft`, () => submitReviewerComments(false))}>
                Save as a Draft
            </Button>
        </Grid>

        <Grid size={{ xs: 12, sm: 3, md: 2, lg: 1.5 }}>
            <Button variant="contained" color="success" onClick={portalAction(portalScreenContext(PortalTelemetryScreen.ReviewerComments), `${PortalTelemetryScreen.ReviewerComments}.onClick.submitReviewerComments.submit`, () => submitReviewerComments(true))}>
                Submit
            </Button>
        </Grid>

        <Grid size={{ xs: 12, sm: 4, md: 3, lg: 2.5 }}>
            <Button component={Link} to={`${AppPathEnum.PCS_PLAN_REVIEW}${window.location.search}`} variant="outlined" onClick={portalAction(portalScreenContext(PortalTelemetryScreen.ReviewerComments), `${PortalTelemetryScreen.ReviewerComments}.navigate`, () => { })}>
                Cancel
            </Button>
        </Grid>

        <Grid size={{ lg: 2 }}></Grid>

        <Grid size={{ xs: 12, sm: 12, md: 4, lg: 3.5 }}>
            <Button variant="contained" color="primary" onClick={portalAction(portalScreenContext(PortalTelemetryScreen.ReviewerComments), `${PortalTelemetryScreen.ReviewerComments}.onClick.setDeclineReviewConfirmation`, () => setDeclineReviewConfirmation(true))}>
                Decline to review
            </Button>
        </Grid>

        <Grid size={{ xs: 12 }}></Grid>

        {declineReviewConfirmation &&
            <UnsavedChangesDialog showDialog={true}
                title={'Are you sure you want to decline to review?'}
                subTitle={`Clicking yes will disappear the record from reviewer list.`}
                yesButtonClick={portalAction(portalScreenContext(PortalTelemetryScreen.ReviewerComments), `${PortalTelemetryScreen.ReviewerComments}.yesButtonClick.declineReview`, () => {
                    declineReview();
                    setDeclineReviewConfirmation(false);
                })}
                noButtonClick={portalAction(portalScreenContext(PortalTelemetryScreen.ReviewerComments), `${PortalTelemetryScreen.ReviewerComments}.noButtonClick.setDeclineReviewConfirmation`, () => setDeclineReviewConfirmation(false))}
                showYesNoButtons={true} />}
    </>
}